import {
    DirectionsBus,
    ExpandLess,
    ExpandMore,
    Person,
    School,
} from "@mui/icons-material";
import {
    Box,
    Avatar,
    ButtonBase,
    Divider,
    Drawer,
    Stack,
    Typography,
} from "@mui/material";
import { useMemo, useState } from "react";

import { useLocations } from "./LocationContext";
import { useGetAllSchoolTransportationByLicenseQuery } from "../../../services/SchoolTransportation/hooks/useQuery";
import type { ISchoolTransportationProps } from "../../../services/SchoolTransportation/types";
import type { ISelectionOption } from "../../../services/types";

interface LocationVehicleRosterPanelProps {
    open: boolean;
    onClose: () => void;
}

function normalizePlate(plate?: string) {
    return plate?.trim().toUpperCase() || "";
}

function sortPeople(items: ISelectionOption[]) {
    return [...items]
        .sort((left, right) =>
            left.name.localeCompare(right.name, "pt-BR", { sensitivity: "base" }),
        );
}

function PersonTile({
    person,
    type,
}: {
    person: ISelectionOption;
    type: "responsible" | "student";
}) {
    const isStudent = type === "student";

    return (
        <Box
            sx={{
                minHeight: 116,
                p: 1,
                borderRadius: 1.5,
                backgroundColor: "var(--app-surface-1)",
                border: "1px solid rgba(255,255,255,0.04)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                textAlign: "center",
            }}
        >
            <Avatar
                src={person.image || undefined}
                alt={person.name}
                variant="rounded"
                sx={{
                    width: 58,
                    height: 58,
                    borderRadius: 1.5,
                    bgcolor: isStudent
                        ? "rgba(56, 189, 248, 0.16)"
                        : "rgba(148, 163, 184, 0.18)",
                    color: isStudent ? "#38bdf8" : "#94a3b8",
                }}
            >
                {isStudent ? (
                    <School sx={{ fontSize: 24 }} />
                ) : (
                    <Person sx={{ fontSize: 24 }} />
                )}
            </Avatar>
            <Typography
                variant="caption"
                sx={{
                    color: "#e2e8f0",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    width: "100%",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                }}
            >
                {person.name}
            </Typography>
        </Box>
    );
}

export default function LocationVehicleRosterPanel({
    open,
    onClose,
}: LocationVehicleRosterPanelProps) {
    const locations = useLocations();
    const [expandedSections, setExpandedSections] = useState<
        Record<string, { students: boolean; responsibles: boolean }>
    >({});

    const { data: transportations = [] } =
        useGetAllSchoolTransportationByLicenseQuery(
            { pageNumber: 1, pageSize: 9999 },
            { name: "" },
        );

    const transportationByPlate = useMemo(() => {
        const map = new Map<string, ISchoolTransportationProps>();
        transportations.forEach((transportation) => {
            map.set(normalizePlate(transportation.plate), transportation);
        });
        return map;
    }, [transportations]);

    const roster = useMemo(() => {
        return locations
            .filter((location) => normalizePlate(location.vehiclePlate))
            .map((location) => {
                const linkedTransportation = transportationByPlate.get(
                    normalizePlate(location.vehiclePlate),
                );
                return {
                    location,
                    linkedTransportation,
                };
            })
            .sort((left, right) => {
                const leftPlate =
                    normalizePlate(left.location.vehiclePlate) || left.location.entityId;
                const rightPlate =
                    normalizePlate(right.location.vehiclePlate) ||
                    right.location.entityId;
                return leftPlate.localeCompare(rightPlate);
            });
    }, [locations, transportationByPlate]);

    const toggleSection = (
        vehicleId: string,
        section: "students" | "responsibles",
    ) => {
        setExpandedSections((previous) => ({
            ...previous,
            [vehicleId]: {
                students: previous[vehicleId]?.students ?? false,
                responsibles: previous[vehicleId]?.responsibles ?? false,
                [section]: !(previous[vehicleId]?.[section] ?? false),
            },
        }));
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: "100vw", sm: 440 },
                    maxWidth: "100vw",
                    background: "var(--app-surface-2)",
                    color: "white",
                },
            }}
        >
            <Box sx={{ p: 3, borderBottom: "1px solid rgba(148,163,184,0.18)" }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Vínculos por veículo
                </Typography>
                <Typography variant="body2" sx={{ color: "#cbd5e1" }}>
                    Resumo dos alunos e responsáveis ligados a cada veículo.
                </Typography>
            </Box>

            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                {roster.length === 0 ? (
                    <Typography sx={{ color: "#cbd5e1" }}>
                        Nenhum veículo com placa carregado.
                    </Typography>
                ) : (
                    roster.map(({ location, linkedTransportation }) => {
                        const students = linkedTransportation?.students ?? [];
                        const responsibles = linkedTransportation?.responsibles ?? [];

                        return (
                            <Box
                                key={location.entityId}
                                sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    border: "1px solid var(--app-table-border)",
                                    backgroundColor: "var(--app-surface-header)",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                        backgroundColor: "var(--app-surface-2)",
                                        borderColor: "var(--app-table-border)",
                                    },
                                }}
                            >
                                <Stack spacing={2}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Box
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 1.5,
                                                    backgroundColor: "rgba(37, 99, 235, 0.15)",
                                                    color: "#60a5fa",
                                                    display: "flex",
                                                }}
                                            >
                                                <DirectionsBus fontSize="small" />
                                            </Box>
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 900,
                                                        fontSize: "1.05rem",
                                                        letterSpacing: 0.5,
                                                    }}
                                                >
                                                    {location.vehiclePlate?.toUpperCase() ||
                                                        location.entityId}
                                                </Typography>
                                                {linkedTransportation && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: "#60a5fa",
                                                            fontWeight: 700,
                                                            display: "block",
                                                            mt: -0.5,
                                                        }}
                                                    >
                                                        {linkedTransportation.name}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Box>

                                    <Divider sx={{ borderColor: "rgba(148,163,184,0.1)" }} />

                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        <ButtonBase
                                            onClick={() =>
                                                toggleSection(location.entityId, "responsibles")
                                            }
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                width: "100%",
                                                px: 1.5,
                                                py: 1,
                                                borderRadius: 1.5,
                                                backgroundColor: "var(--app-surface-2)",
                                                color: "inherit",
                                                textAlign: "left",
                                                "&:hover": { backgroundColor: "var(--app-accent-bg)" },
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: "#cbd5e1",
                                                    fontWeight: 700,
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                Responsáveis ({responsibles.length})
                                            </Typography>
                                            {expandedSections[location.entityId]?.responsibles ? (
                                                <ExpandLess fontSize="small" sx={{ color: "#94a3b8" }} />
                                            ) : (
                                                <ExpandMore fontSize="small" sx={{ color: "#94a3b8" }} />
                                            )}
                                        </ButtonBase>

                                        {expandedSections[location.entityId]?.responsibles && (
                                            <Box
                                                sx={{
                                                    mt: 0.5,
                                                    px: 0.5,
                                                    display: "grid",
                                                    gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                                                    gap: 1,
                                                }}
                                            >
                                                {sortPeople(responsibles).length === 0 ? (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: "#64748b",
                                                            fontStyle: "italic",
                                                            fontSize: "0.85rem",
                                                            p: 1,
                                                        }}
                                                    >
                                                        Sem responsáveis vinculados
                                                    </Typography>
                                                ) : (
                                                    sortPeople(responsibles).map((responsible) => (
                                                        <PersonTile
                                                            key={responsible.id}
                                                            person={responsible}
                                                            type="responsible"
                                                        />
                                                    ))
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        <ButtonBase
                                            onClick={() =>
                                                toggleSection(location.entityId, "students")
                                            }
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                width: "100%",
                                                px: 1.5,
                                                py: 1,
                                                borderRadius: 1.5,
                                                backgroundColor: "var(--app-surface-2)",
                                                color: "inherit",
                                                textAlign: "left",
                                                "&:hover": { backgroundColor: "var(--app-accent-bg)" },
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: "#cbd5e1",
                                                    fontWeight: 700,
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                Alunos ({students.length})
                                            </Typography>
                                            {expandedSections[location.entityId]?.students ? (
                                                <ExpandLess fontSize="small" sx={{ color: "#94a3b8" }} />
                                            ) : (
                                                <ExpandMore fontSize="small" sx={{ color: "#94a3b8" }} />
                                            )}
                                        </ButtonBase>

                                        {expandedSections[location.entityId]?.students && (
                                            <Box
                                                sx={{
                                                    mt: 0.5,
                                                    px: 0.5,
                                                    display: "grid",
                                                    gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                                                    gap: 1,
                                                }}
                                            >
                                                {sortPeople(students).length === 0 ? (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: "#64748b",
                                                            fontStyle: "italic",
                                                            fontSize: "0.85rem",
                                                            p: 1,
                                                        }}
                                                    >
                                                        Sem alunos vinculados
                                                    </Typography>
                                                ) : (
                                                    sortPeople(students).map((student) => (
                                                        <PersonTile
                                                            key={student.id}
                                                            person={student}
                                                            type="student"
                                                        />
                                                    ))
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </Stack>
                            </Box>
                        );
                    })
                )}

            </Box>
        </Drawer>
    );
}
